/**
 * Header CEP picker: toggles an inline field, validates an 8-digit Brazilian CEP,
 * stores it in localStorage and shows it in the trigger label.
 */
const STORAGE_KEY = 'cisel:cep';

class CepPicker extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.trigger = this.querySelector('[data-cep-trigger]');
    this.panel = this.querySelector('[data-cep-panel]');
    this.form = this.querySelector('form');
    this.input = this.querySelector('input');
    this.label = this.querySelector('[data-cep-label]');
    this.error = this.querySelector('[data-cep-error]');

    this.#render(this.#read());

    this.trigger?.addEventListener('click', () => this.#toggle(), { signal });
    this.input?.addEventListener('input', () => {
      this.input.value = this.#format(this.input.value);
      this.#setError(false);
    }, { signal });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#save();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.#close();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.#close();
        this.trigger?.focus();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #read() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  #format(value) {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  #toggle() {
    if (this.panel.hidden) {
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.input.value = this.#read();
      this.input.focus();
    } else {
      this.#close();
    }
  }

  #close() {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.#setError(false);
  }

  #setError(show) {
    if (!this.error) return;
    this.error.hidden = !show;
    this.input?.setAttribute('aria-invalid', show ? 'true' : 'false');
  }

  #save() {
    const cep = this.#format(this.input.value);
    if (cep.replace(/\D/g, '').length !== 8) {
      this.#setError(true);
      this.input.focus();
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, cep);
    } catch {
      /* storage unavailable: still show the value for this page view */
    }
    this.#render(cep);
    this.#close();
    this.trigger?.focus();
  }

  #render(cep) {
    if (!this.label) return;
    this.label.textContent = cep ? `${this.dataset.savedPrefix || ''} ${cep}`.trim() : this.dataset.emptyLabel || '';
  }
}

if (!customElements.get('s-cep-picker')) customElements.define('s-cep-picker', CepPicker);
